import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  Paper,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  AlertTitle,
  alpha,
} from '@mui/material';
import {
  Add,
  MoreVert,
  EditOutlined,
  DeleteOutline,
  ChevronRight,
  ShieldOutlined,
  PaymentsOutlined,
  EventRepeatOutlined,
  Search,
  Inventory2Outlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import {
  POLICY_TYPES,
  PolicyTypeIcon,
  policyType,
  policyStatus,
  frequency,
  dueInfo,
  hasEnded,
  summarisePolicies,
  formatCover,
  formatRupees,
  DUE_SOON_DAYS,
} from '../../utils/insurance';
import { SummaryTile } from '../utilities/FastagManager';
import PolicyDialog from './PolicyDialog';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

// Due / overdue first, then by next due date, then newest
const sortPolicies = (a, b) => {
  const da = dueInfo(a)?.days ?? Infinity;
  const db = dueInfo(b)?.days ?? Infinity;
  if (da !== db) return da - db;
  return String(b.created_at).localeCompare(String(a.created_at));
};

const Insurance = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const insuranceTour = useTour('insurance', { ready: !loading });

  const [tab, setTab] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPolicy, setMenuPolicy] = useState(null);
  const [deletePolicy, setDeletePolicy] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchPolicies = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('insurance_policies')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching insurance policies:', error);
      notify('Failed to fetch insurance policies', 'error');
      setPolicies([]);
    } else {
      setPolicies(data || []);
    }
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const activePolicies = useMemo(() => policies.filter(p => p.status === 'active'), [policies]);
  const inactivePolicies = useMemo(() => policies.filter(p => p.status !== 'active'), [policies]);
  const totals = useMemo(() => summarisePolicies(policies), [policies]);
  const duePolicies = useMemo(
    () => activePolicies.filter(p => { const due = dueInfo(p); return due && due.days <= DUE_SOON_DAYS; }).sort(sortPolicies),
    [activePolicies],
  );

  const tabPolicies = tab === 'active' ? activePolicies : inactivePolicies;
  const typesInTab = useMemo(() => POLICY_TYPES.filter(t => tabPolicies.some(p => p.policy_type === t.value)), [tabPolicies]);
  const shownPolicies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tabPolicies
      .filter(p => typeFilter === 'all' || p.policy_type === typeFilter)
      .filter(p => !query || [p.policy_name, p.insurer, p.policy_number, p.insured, p.nominee]
        .some(field => (field || '').toLowerCase().includes(query)))
      .sort(sortPolicies);
  }, [tabPolicies, typeFilter, search]);

  // Reset a type filter that no longer matches anything in this tab
  useEffect(() => {
    if (typeFilter !== 'all' && !typesInTab.some(t => t.value === typeFilter)) setTypeFilter('all');
  }, [typesInTab, typeFilter]);

  const openAdd = () => { setEditingPolicy(null); setDialogOpen(true); };
  const openEdit = (policy) => { setEditingPolicy(policy); setDialogOpen(true); };
  const handleSaved = (message) => {
    setDialogOpen(false);
    setEditingPolicy(null);
    notify(message);
    fetchPolicies();
  };

  const handleDelete = async () => {
    const policy = deletePolicy;
    setDeletePolicy(null);
    const { error } = await supabase.from('insurance_policies').delete().eq('id', policy.id).eq('user_id', user.id);
    if (error) {
      console.error('Error deleting insurance policy:', error);
      notify('Failed to delete the policy', 'error');
      return;
    }
    notify('Policy deleted successfully');
    fetchPolicies();
  };

  const openMenu = (event, policy) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuPolicy(policy);
  };
  const closeMenu = () => setMenuAnchor(null);

  // ---- Render ----
  const emptyState = policies.length === 0 ? (
    <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
      <Box sx={{ width: 88, height: 88, borderRadius: '50%', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.main' }}>
        <ShieldOutlined sx={{ fontSize: 44 }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>No Policies Added</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 440, mx: 'auto' }}>
        Add your term, health, motor and other insurance policies to see your total cover, yearly premium and upcoming renewals in one place.
      </Typography>
      <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Your First Policy</Button>
    </Card>
  ) : (
    <Card sx={{ p: 5, textAlign: 'center' }}>
      {tab === 'active' && !search && typeFilter === 'all' ? (
        <>
          <ShieldOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>No Active Policies</Typography>
          <Typography variant="body2" color="text.secondary">Lapsed, matured, surrendered and expired policies are in the Inactive tab.</Typography>
        </>
      ) : tab === 'inactive' && !search && typeFilter === 'all' ? (
        <>
          <Inventory2Outlined sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>No Inactive Policies</Typography>
          <Typography variant="body2" color="text.secondary">Policies you mark as lapsed, matured, surrendered or expired appear here.</Typography>
        </>
      ) : (
        <Typography color="text.secondary">No policies match your search.</Typography>
      )}
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Insurance</Typography>
          <Typography variant="body2" color="text.secondary">Your policies, cover and premium renewals</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={insuranceTour.start} />
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} data-tour="insurance-add">Add Policy</Button>
        </Box>
      </Box>

      {!loading && activePolicies.length > 0 && (
        <Paper data-tour="insurance-summary" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
          <SummaryTile icon={<ShieldOutlined />} color="primary" value={totals.count} label={totals.count === 1 ? 'Active policy' : 'Active policies'} />
          <SummaryTile icon={<ShieldOutlined />} color="success" value={formatCover(totals.cover)} label={`Total cover${totals.lifeCover ? ` · Life ${formatCover(totals.lifeCover)}` : ''}`} />
          <SummaryTile icon={<PaymentsOutlined />} color="info" value={formatRupees(totals.annualPremium)} label="Premium per year" />
          <SummaryTile
            icon={<EventRepeatOutlined />}
            color={totals.dueCount ? 'warning' : 'info'}
            value={totals.dueCount ? formatRupees(totals.dueAmount) : '—'}
            label={totals.dueCount ? `Due in ${DUE_SOON_DAYS} days (${totals.dueCount})` : `Nothing due in ${DUE_SOON_DAYS} days`}
          />
        </Paper>
      )}

      {/* Upcoming / overdue premiums */}
      {!loading && duePolicies.length > 0 && (
        <Alert
          severity={duePolicies.some(p => dueInfo(p).days <= 0) ? 'error' : 'warning'}
          icon={<EventRepeatOutlined />}
          sx={{ mb: 2.5, '& .MuiAlert-message': { width: '100%' } }}
          data-tour="insurance-due"
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Premiums due soon</AlertTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {duePolicies.slice(0, 5).map(p => (
              <Box
                key={p.id}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/insurance/${p.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/insurance/${p.id}`); }}
                sx={{
                  display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' },
                  columnGap: 1, cursor: 'pointer', '&:hover .due-name': { textDecoration: 'underline' },
                  '& + &': { mt: { xs: 0.75, sm: 0 } },
                }}
              >
                <Typography variant="body2" className="due-name" sx={{ fontWeight: 600, flex: 1, minWidth: 0, maxWidth: '100%' }} noWrap>
                  {p.policy_name}{p.insurer ? ` · ${p.insurer}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {formatRupees(p.premium_amount)} · {dueInfo(p).label}
                </Typography>
              </Box>
            ))}
            {duePolicies.length > 5 && (
              <Typography variant="caption">+{duePolicies.length - 5} more below</Typography>
            )}
          </Box>
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, value) => setTab(value)} data-tour="insurance-tabs" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="active" label={`Active (${activePolicies.length})`} />
        <Tab value="inactive" label={`Inactive (${inactivePolicies.length})`} />
      </Tabs>

      {/* Filters */}
      {tabPolicies.length > 0 && (
        <Box data-tour="insurance-filters" sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search name, insurer, policy no…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
          />
          {typesInTab.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip label="All" color={typeFilter === 'all' ? 'primary' : 'default'} variant={typeFilter === 'all' ? 'filled' : 'outlined'} onClick={() => setTypeFilter('all')} />
              {typesInTab.map(t => (
                <Chip
                  key={t.value}
                  icon={<PolicyTypeIcon type={t.value} />}
                  label={t.label}
                  color={typeFilter === t.value ? 'primary' : 'default'}
                  variant={typeFilter === t.value ? 'filled' : 'outlined'}
                  onClick={() => setTypeFilter(t.value)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : shownPolicies.length === 0 ? (
        emptyState
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {shownPolicies.map((policy) => {
            const type = policyType(policy.policy_type);
            const status = policyStatus(policy.status);
            const due = dueInfo(policy);
            const ended = hasEnded(policy);
            const inactive = policy.status !== 'active';
            const freq = frequency(policy.premium_frequency);
            return (
              <Card key={policy.id} data-tour="insurance-card" sx={{ position: 'relative', borderRadius: 3, opacity: inactive ? 0.85 : 1, '&:hover': { boxShadow: 'var(--fx-card-shadow-hover)' } }}>
                <CardActionArea onClick={() => navigate(`/insurance/${policy.id}`)} sx={{ pr: 5 }}>
                  <Box sx={{ p: 2.25, display: 'flex', gap: 2, width: '100%' }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                      bgcolor: alpha(type.color, inactive ? 0.06 : 0.1), color: inactive ? 'text.secondary' : type.color,
                    }}>
                      <PolicyTypeIcon type={policy.policy_type} sx={{ fontSize: 26 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>{policy.policy_name}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {[type.label, policy.insurer].filter(Boolean).join(' · ')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="div">Cover</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatCover(policy.sum_assured)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="div">Premium</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {formatRupees(policy.premium_amount)}
                            <Typography component="span" variant="caption" color="text.secondary">{freq.short}</Typography>
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25, flexWrap: 'wrap' }}>
                        {inactive && <Chip size="small" label={status.label} color={status.color} variant="outlined" />}
                        {due && <Chip size="small" label={due.label} color={due.severity} variant={due.severity === 'default' ? 'outlined' : 'filled'} />}
                        {ended && <Chip size="small" label="End date passed" color="warning" variant="outlined" />}
                      </Box>
                    </Box>
                    <ChevronRight sx={{ color: 'text.secondary', alignSelf: 'center' }} />
                  </Box>
                </CardActionArea>
                <IconButton
                  size="small"
                  onClick={(e) => openMenu(e, policy)}
                  aria-label={`Options for ${policy.policy_name}`}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Policy options */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { closeMenu(); openEdit(menuPolicy); }}>
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
          Edit Policy
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); setDeletePolicy(menuPolicy); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
          Delete Policy
        </MenuItem>
      </Menu>

      <PolicyDialog
        open={dialogOpen}
        policy={editingPolicy}
        onClose={() => { setDialogOpen(false); setEditingPolicy(null); }}
        onSaved={handleSaved}
        supabase={supabase}
        user={user}
      />

      {/* Delete confirmation */}
      <Dialog open={Boolean(deletePolicy)} onClose={() => setDeletePolicy(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Policy</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{deletePolicy?.policy_name}" and all its recorded premiums? This can't be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            If the policy has only ended, edit it and change its status (for example to Matured or Expired) to keep its history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePolicy(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <GuidedTour steps={TOURS.insurance} open={insuranceTour.open} onClose={insuranceTour.close} />
    </Box>
  );
};

export default Insurance;
